import { FormFieldType } from '@/types'

export interface OptionItem {
  id: string
  label: string
  value: string
}

const createOptionId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `opt-${Date.now()}-${Math.random().toString(16).slice(2)}`

const optionFieldTypes = new Set<FormFieldType>([
  FormFieldType.SINGLE_SELECT,
  FormFieldType.MULTI_SELECT,
  FormFieldType.DROPDOWN,
  FormFieldType.RATING,
])

export const supportsOptions = (type: FormFieldType) => optionFieldTypes.has(type)

export const slugify = (input: string, fallback = 'option') => {
  const normalized = input.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-')
  const collapsed = normalized.replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '')
  return collapsed || fallback
}

export const generateOptionValue = (label: string, index: number) =>
  `${slugify(label, `option-${index + 1}`)}-${index}`

export const sanitizeOptions = (options: OptionItem[]): OptionItem[] =>
  options
    .map((option, index) => ({
      id: option.id ?? createOptionId(),
      label: option.label.trim(),
      value: option.value.trim() || generateOptionValue(option.label, index),
    }))
    .filter(option => option.label.length > 0)

export const validateOptions = (options: OptionItem[]): string | null => {
  if (options.length === 0) {
    return '請至少建立一個有效的選項'
  }

  const seen = new Set<string>()
  for (const option of options) {
    if (seen.has(option.value)) {
      return '選項的值必須唯一，請調整重複項目'
    }
    seen.add(option.value)
  }

  return null
}

export const processOptions = (options: OptionItem[]) => {
  const sanitized = sanitizeOptions(options)
  const error = validateOptions(sanitized)
  return { sanitized, error }
}

export const buildOptionsFromText = (input: string, startIndex = 0): OptionItem[] => {
  const lines = input
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0)

  return lines.map((label, index) => ({
    id: createOptionId(),
    label,
    value: generateOptionValue(label, startIndex + index),
  }))
}

export const createOptionItem = (label: string, value: string): OptionItem => ({
  id: createOptionId(),
  label,
  value,
})

export const ensureOptionId = (option: OptionItem): OptionItem =>
  option.id ? option : { ...option, id: createOptionId() }
